import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClaimsPanel = ({ claims, onSubmitClaim, onAppealClaim }) => {
  const [activeTab, setActiveTab] = useState('pending');

  const getClaimStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success border-success';
      case 'denied':
        return 'bg-destructive/10 text-destructive border-destructive';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'submitted':
        return 'bg-primary/10 text-primary border-primary';
      case 'appealed':
        return 'bg-accent/10 text-accent border-accent';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const filteredClaims = claims?.filter(claim => {
    if (activeTab === 'pending') return claim?.status === 'pending' || claim?.status === 'submitted';
    if (activeTab === 'denied') return claim?.status === 'denied' || claim?.status === 'appealed';
    if (activeTab === 'approved') return claim?.status === 'approved';
    return true;
  });

  const tabs = [
    { id: 'pending', label: 'Pending', count: claims?.filter(c => c?.status === 'pending' || c?.status === 'submitted')?.length },
    { id: 'denied', label: 'Denied', count: claims?.filter(c => c?.status === 'denied' || c?.status === 'appealed')?.length },
    { id: 'approved', label: 'Approved', count: claims?.filter(c => c?.status === 'approved')?.length }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Claims Management</h2>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={onSubmitClaim}
          >
            Submit Claim
          </Button>
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap flex-shrink-0 ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {tab?.label}
              <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
                activeTab === tab?.id
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-background text-foreground'
              }`}>
                {tab?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {filteredClaims?.map((claim) => (
          <div key={claim?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-smooth">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="FileText" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground mb-1">{claim?.patientName}</h3>
                    <p className="text-sm text-muted-foreground">Claim #{claim?.claimNumber}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getClaimStatusColor(claim?.status)} flex-shrink-0`}>
                    {claim?.status === 'approved' && <Icon name="CheckCircle" size={12} />}
                    {claim?.status === 'denied' && <Icon name="XCircle" size={12} />}
                    {claim?.status?.charAt(0)?.toUpperCase() + claim?.status?.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Service Date</p>
                    <p className="text-sm font-medium text-foreground">{claim?.serviceDate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Claim Amount</p>
                    <p className="text-sm font-semibold text-foreground">${claim?.claimAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Insurance</p>
                    <p className="text-sm font-medium text-foreground">{claim?.insuranceProvider}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">CPT Code</p>
                    <p className="text-sm font-mono text-foreground">{claim?.cptCode}</p>
                  </div>
                </div>

                {claim?.diagnoses && claim?.diagnoses?.length > 0 && (
                  <div className="mb-3 p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Icon name="Stethoscope" size={12} />
                      Diagnosis Codes (ICD-10)
                    </p>
                    <div className="space-y-1.5">
                      {claim?.diagnoses?.map((diagnosis, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">
                            {diagnosis?.code}
                          </span>
                          <span className="text-xs text-foreground flex-1">{diagnosis?.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {claim?.denialReason && (
                  <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                    <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-destructive mb-1">Denial Reason</p>
                      <p className="text-sm text-destructive/90">{claim?.denialReason}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                {claim?.status === 'denied' && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={() => onAppealClaim(claim)}
                    className="flex-1 lg:flex-none"
                  >
                    Appeal
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  className="flex-1 lg:flex-none"
                >
                  View Details
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredClaims?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <Icon name="FileText" size={48} className="text-muted-foreground mb-4" />
          <p className="text-foreground font-medium mb-1">No claims found</p>
          <p className="text-sm text-muted-foreground">No {activeTab} claims at this time</p>
        </div>
      )}
    </div>
  );
};

export default ClaimsPanel;